// ─── Hook : GARDE calculée à l'affichage + vacances/fériés ────────
// Extrait d'EcranCalendrier (découpage). Écoute la garde stockée, les parents,
// les rythmes et les demandes ; charge la config vacances/fériés ; CALCULE la
// garde de chaque jour visible à la volée (jamais écrite en base — seules les
// surcharges vivent dans jours/{dk}) ; propose/bascule un changement de garde.
import { useEffect, useMemo, useState } from 'react';
import { useAuth, useT, confirmerChoix } from '@mop/core';
import { ecouterGarde, setGardeJour, lireParents, GARDE_COL_DEFAUT, COL_EXC } from '../services/garde';
import { lireRythmes, construireConfig } from '../services/rythmesGarde';
import { gardePourDate } from '../services/modelesGarde';
import { chargerVacancesConfig, gardeVacancesJour } from '../services/vacances';
import { dateKey } from '../services/evenements';
import { nomFerie } from '../services/joursFeries';
import {
  ecouterDemandes, compterDemandesEnAttente, creerDemandeGarde, enregistrerGardeAutoAcquittee,
} from '../services/demandes';

const dkDe = (d) => dateKey(d.getFullYear(), d.getMonth(), d.getDate());

export default function useGardeAffichage({
  calId, edit, estCoparent, gardeVisible, vue, an, mo, sel, cases, semaineDates, rechargeVacances,
}) {
  const t = useT();
  const { user } = useAuth();
  const [garde, setGarde] = useState({});          // { dk: 'parent1'|'parent2' } (surcharges stockées)
  const [parents, setParents] = useState(null);
  const [rythmesCal, setRythmesCal] = useState({ rythmes: [], perso: [] });
  const [demandes, setDemandes] = useState([]);    // demandes de changement de garde
  const [erreurDemandes, setErreurDemandes] = useState('');
  const [vacPeriodes, setVacPeriodes] = useState([]);
  const [vacCfg, setVacCfg] = useState(null);      // config complète (schéma garde + périodes)
  const [feriesMap, setFeriesMap] = useState(null);// { dk: nom } si géré ; null = calcul FR
  const montrerGarde = estCoparent && gardeVisible;

  // Garde par jour (temps réel).
  useEffect(() => {
    if (!calId) { setGarde({}); return undefined; }
    return ecouterGarde(calId, setGarde);
  }, [calId]);
  // Parents + rythmes : rechargés aussi à la fermeture des pages Gardes /
  // Vacances (rechargeVacances change) — remplace l'ancien onFermer manuel.
  useEffect(() => {
    if (!calId) return;
    lireParents(calId).then(setParents).catch(() => {});
    lireRythmes(calId).then(setRythmesCal).catch(() => {});
  }, [calId, rechargeVacances]);

  // Demandes de changement de garde (partagées MoPCal).
  useEffect(() => {
    if (!calId) { setDemandes([]); setErreurDemandes(''); return undefined; }
    setErreurDemandes('');
    return ecouterDemandes(calId, setDemandes, setErreurDemandes);
  }, [calId]);

  // Périodes de vacances (barre fine) + jours fériés (liste gérée ou calcul).
  useEffect(() => {
    if (!calId) { setVacPeriodes([]); setVacCfg(null); setFeriesMap(null); return; }
    chargerVacancesConfig(calId).then(cfg => {
      setVacCfg(cfg || null);
      setVacPeriodes(Array.isArray(cfg?.periodes) ? cfg.periodes.filter(p => p.actif !== false) : []);
      // Si des fériés sont explicitement gérés, ils font foi ; sinon calcul FR.
      setFeriesMap(Array.isArray(cfg?.feries) && cfg.feries.length
        ? Object.fromEntries(cfg.feries.map(f => [f.date, f.nom])) : null);
    }).catch(() => { setVacPeriodes([]); setVacCfg(null); setFeriesMap(null); });
  }, [calId, rechargeVacances]);

  const estVacances = (dk) => vacPeriodes.some(p => p.debut <= dk && dk <= p.fin);
  const ferieNom = (dk) => (feriesMap ? (feriesMap[dk] || null) : nomFerie(dk, t));
  const estFerie = (dk) => !!ferieNom(dk);

  const nbDemandes = compterDemandesEnAttente(demandes, user?.uid);
  // Couleurs de garde choisies par l'utilisateur (repli sur les couleurs par défaut).
  const gardeCol = {
    parent1: parents?.couleur1 || GARDE_COL_DEFAUT.parent1,
    parent2: parents?.couleur2 || GARDE_COL_DEFAUT.parent2,
  };

  // Rythmes actifs triés + config précalculée (application paresseuse).
  const rythmeConfigs = useMemo(() =>
    [...(rythmesCal.rythmes || [])]
      .filter(p => p.applique !== false)                       // toggle « Appliquer » par rythme
      .sort((a, b) => (a.debut < b.debut ? -1 : a.debut > b.debut ? 1 : 0))
      .map(p => ({ p, config: construireConfig(p.modeleId, p.debut, rythmesCal.perso) })),
    [rythmesCal]);
  // Ordre de priorité : surcharge stockée > vacances (schéma global) > rythme.
  const gardeCalculee = (dk) => {
    const gv = gardeVacancesJour(dk, vacCfg);                  // répartition vacances (calculée)
    if (gv) return gv;
    const item = rythmeConfigs.find(({ p }) => p.debut <= dk && (!p.fin || dk <= p.fin));
    return item?.config ? gardePourDate(item.config, dk) : null;
  };
  const gardeAff = useMemo(() => {
    if (!montrerGarde) return {};
    const out = { ...garde };                                  // surcharges stockées (priment)
    const dks = new Set();
    cases.forEach(d => dks.add(dkDe(d)));                       // vue mois (42 jours)
    semaineDates.forEach(d => dks.add(dkDe(d)));                // vue semaine
    dks.add(dateKey(an, mo, sel));                             // vue jour
    for (const dk of dks) { if (out[dk]) continue; const g = gardeCalculee(dk); if (g) out[dk] = g; }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [montrerGarde, garde, rythmeConfigs, vacCfg, cases, semaineDates, an, mo, sel]);
  // Jours visibles (Semaine/Jour) avec un ÉCHANGE de garde → couleur du TEMPLATE
  // pour l'anneau d'en-tête (comme la grille du mois).
  const excColVisible = useMemo(() => {
    if (!montrerGarde) return {};
    const out = {};
    const jours = vue === 'jour' ? [new Date(an, mo, sel)] : semaineDates;
    for (const d of jours) { const dk = dkDe(d); if (garde[dk]) { const tpl = gardeCalculee(dk); if (tpl && garde[dk] !== tpl) out[dk] = gardeCol[tpl] || COL_EXC; } }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [montrerGarde, garde, rythmeConfigs, vacCfg, semaineDates, vue, an, mo, sel]);

  // Propose (ou applique) un changement de garde pour un jour.
  // 2 parents déclarés → demande à acquitter par l'autre ; parent unique → direct.
  async function proposerGarde(dk, g) {
    if (!edit || !calId || !estCoparent) return;
    // On raisonne sur la garde AFFICHÉE (surcharge stockée OU template calculé).
    const actuelle = gardeAff[dk] || null;
    if (actuelle === g) return;   // pas de changement visible
    // Revenir exactement au template = supprimer la surcharge (immédiat).
    const template = gardeCalculee(dk) || null;
    if (g === template) { try { await setGardeJour(calId, dk, null); } catch (_) {} return; }
    const p1 = parents?.parent1, p2 = parents?.parent2;
    const moi = user?.uid;
    const nom = user?.prenom || user?.email || '';
    const autreUid = (p1?.uid && p1.uid !== moi) ? p1.uid : (p2?.uid && p2.uid !== moi ? p2.uid : null);
    const autreRole = autreUid && autreUid === p1?.uid ? 'parent1' : 'parent2';
    // Demandes désactivées (config) → changement appliqué directement même à deux parents.
    const demandesActives = parents?.demandesActives !== false;
    try {
      if (!autreUid || !demandesActives) {
        await enregistrerGardeAutoAcquittee({ calId, dateKey: dk, gardeActuelle: actuelle, gardeProposee: g, demandePar: moi, demandeParNom: nom });
      } else {
        await creerDemandeGarde({ calId, dateKey: dk, gardeActuelle: actuelle, gardeProposee: g, demandePar: moi, demandeParNom: nom, autreParentUid: autreUid, autreParentRole: autreRole });
        confirmerChoix(`${t('profil.demandeEnvoyeeTitre')} 🧷`, t('calendrier.demandeGardeEnvoyeeMsg', { date: dk, nom: autreRole === 'parent1' ? (p1?.nom || t('garde.parent1')) : (p2?.nom || t('garde.parent2')) }), [{ text: t('commun.ok'), style: 'cancel' }]);
      }
    } catch (_) {}
  }
  // BASCULE la garde d'un jour entre les deux parents (à partir de la garde
  // AFFICHÉE). Revenir à la garde du template efface la surcharge.
  function basculerGarde(d) {
    const dk = dkDe(d);
    if (!edit || !montrerGarde || !parents) return;
    const suivant = (gardeAff[dk] || null) === 'parent1' ? 'parent2' : 'parent1';
    proposerGarde(dk, suivant);
  }

  // Recharge manuelle des parents — utilisée après l'enregistrement de la
  // config calendrier (FeuilleConfigCalendrier écrit `parents` en base sans
  // que `rechargeVacances` change, donc l'effet ci-dessus ne se redéclenche pas).
  const rechargerParents = () => { if (calId) lireParents(calId).then(setParents).catch(() => {}); };

  return {
    garde, parents, gardeCol, demandes, erreurDemandes, nbDemandes, montrerGarde,
    vacPeriodes, vacCfg, feriesMap, estVacances, ferieNom, estFerie,
    gardeCalculee, gardeAff, excColVisible, proposerGarde, basculerGarde, rechargerParents,
  };
}
